import { useState, useEffect, useOptimistic, startTransition } from 'react';
import { Link } from 'react-router-dom';
import { trpc } from '../utils/trpc';
import styles from '../styles/EventInfo.module.css';
import { OrganizerEventStatisticsModal } from './OrganizerEventStatisticsModal';
import { useAuth } from '../contexts/AuthContext';
import CopyIcon from '../images/copy.png';
import ReactDOM from 'react-dom';
import VenueProfile from '../page-components/VenueProfile';
interface EventInfoProps {
  id: string;
  onClose?: () => void;
}

/**
 * A template component to render individual event details
 * @param id specific event identification
 * @param onClose custom close behavior
 * @returns Individual group overview card
 */
export function EventInfo({ id, onClose }: EventInfoProps) {
  const { user } = useAuth();
  const utils = trpc.useUtils();

  // Setup use and trpc references
  const rsvpMutation = trpc.event.rsvpToEvent.useMutation();
  const cancelRsvpMutation = trpc.event.cancelRsvp.useMutation();

  const { data: rsvpStats } = trpc.event.getRsvpStats.useQuery({
    event_id: id,
  });
  const { data: myRsvp } = trpc.event.getMyRsvp.useQuery({ eventId: id });

  // Calculate base states directly from queries
  const baseIsGoing = myRsvp?.status === 'going';
  const baseCount = rsvpStats?.count ?? 0;

  const [optimisticRsvp, addOptimisticRsvp] = useOptimistic(
    { isGoing: baseIsGoing, count: baseCount },
    (currentState, newIsGoing: boolean) => ({
      isGoing: newIsGoing,
      count: newIsGoing ? currentState.count + 1 : Math.max(0, currentState.count - 1),
    }),
  );

  // Use optimistic state for the rest of the component
  const isGoing = optimisticRsvp.isGoing;
  const count = optimisticRsvp.count;

  const [isOpen, setIsOpen] = useState(true);
  const [showTags, setShowTags] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [reported, setReported] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [discountCopied, setDiscountCopied] = useState(false);
  const [venueProfileOpen, setVenueProfileOpen] = useState(false); // Added state for venue modal

  const {
    data: event,
    isLoading: eventLoading,
    error: eventError,
  } = trpc.event.getEventById.useQuery({ eventId: id });

  const { data: venue } = trpc.venue.getById.useQuery(
    { venue_id: event?.venue_id ?? '' },
    { enabled: !!event?.venue_id },
  );
  const { data: group } = trpc.group.getById.useQuery(
    { id: event?.group_id ?? '' },
    { enabled: !!event?.group_id },
  );
  const tags =
    event?.event_tags
      ?.map((et: { tags?: { name: string } | null }) => et?.tags?.name)
      .filter(Boolean) ?? [];
  const attendance = event?.event_attendees?.count ?? 0;

  //gets the members to see if the user should be able to see the discount code
  const mockDiscountCode = 'SOUP10'; // Placeholder discount code until backend is added
  const { data: members = [] } = trpc.group.getMembers.useQuery(
    { groupId: event?.group_id ?? '' },
    { enabled: !!event?.group_id },
  );

  const currentUserRole = members.find(
    (m: { profile_id: string; role: string }) => m.profile_id === user?.id,
  )?.role;

  const isPrivileged = ['owner', 'admin', 'organizer'].includes(
    currentUserRole as string,
  );
  const now = new Date();

  // Safely create Date object only when event exists
  const eventDateObj = event?.date ? new Date(event.date) : null;

  // Start time (string)
  const eventStartTime = eventDateObj
    ? eventDateObj.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  // End time (Date object)
  const eventEndTime =
    eventDateObj && event?.duration_minutes
      ? new Date(eventDateObj.getTime() + event.duration_minutes * 60000)
      : null;

  // End time (string)
  const eventEndTimeFormatted = eventEndTime
    ? eventEndTime.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  // Active event check
  const isEventActive =
    eventDateObj && eventEndTime ? now >= eventDateObj && now <= eventEndTime : false;

  // Date display
  const formattedDate = eventDateObj
    ? eventDateObj.toLocaleDateString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;

  // Time display (range if possible)
  const formattedTime =
    eventStartTime && eventEndTimeFormatted
      ? `${eventStartTime} - ${eventEndTimeFormatted}`
      : eventStartTime;

  const canSeeDiscount = isEventActive && (isPrivileged || isGoing);

  const hasEnded = eventEndTime ? eventEndTime < now : false;

  // Pause scrolling while open
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // Removes render on close
  if (!isOpen) {
    return null;
  }

  // Handle report menu display (visual)
  const handleReport = () => {
    setReported(true);
    setShowMenu(false);
  };

  // Handle front and backend RSVP behavior using React 19 standard actions
  const handleRsvp = () => {
    const newIsGoing = !isGoing;

    startTransition(async () => {
      addOptimisticRsvp(newIsGoing);

      try {
        if (newIsGoing) {
          await rsvpMutation.mutateAsync({ eventId: id, status: 'going' });
        } else {
          await cancelRsvpMutation.mutateAsync({ eventId: id });
        }

        await Promise.all([
          utils.event.getMyRsvp.invalidate({ eventId: id }),
          utils.event.getRsvpStats.invalidate({ event_id: id }),
          utils.event.getEventsByGroupId.invalidate(),
        ]);
      } catch (error) {
        console.error('RSVP action failed:', error);
      }
    });
  };

  // Standard display
  return (
    <div
      className={styles.modalBackdrop}
      onClick={() => {
        if (onClose) onClose();
        else setIsOpen(false);
      }}
    >
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <aside className={styles.panel}>
          <div className={styles.controls}>
            <div className={styles.menuWrap}>
              {/* Options menu (WIP) */}
              <button
                className={styles.iconBtn}
                onClick={() => setShowMenu((v) => !v)}
                aria-label="Options"
              >
                ⋮
              </button>
              {showMenu && (
                <div className={styles.dropdown}>
                  <button
                    className={styles.dropItem}
                    onClick={handleReport}
                    disabled={reported}
                  >
                    {reported ? '✓ Reported' : 'Report Event'}
                  </button>
                </div>
              )}
            </div>

            {/* Close button */}
            <button
              className={styles.closeBtn}
              onClick={() => {
                if (onClose) onClose();
                else setIsOpen(false);
              }}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Details */}
          <div className={styles.panelBody}>
            {/* Loading placeholder */}
            {eventLoading && (
              <div className={styles.skeletonWrap}>
                <div className={styles.skeletonBanner} />
                <div className={styles.skeletonTitle} />
                <div className={styles.skeletonLine} />
                <div className={styles.skeletonLine} style={{ width: '60%' }} />
              </div>
            )}

            {/* Error catch */}
            {eventError && !eventLoading && (
              <p className={styles.errorText}>
                Could not load event: {eventError.message}
              </p>
            )}

            {/* Successful fetch display */}
            {event && !eventLoading && (
              <>
                {/* Banner */}
                {event.banner_url ? (
                  <div className={styles.bannerWrap}>
                    <img
                      onLoad={(e) => e.currentTarget.classList.add('loaded')}
                      src={event.banner_url}
                      alt="Event banner"
                      className={styles.bannerImg}
                    />
                  </div>
                ) : (
                  <div className={styles.bannerPlaceholder} />
                )}

                {/* Data field displays */}
                <div className={styles.cardInner}>
                  <h2 className={styles.eventName}>{event.event_name}</h2>

                  <div className={styles.infoRow}>
                    <div className={styles.infoField}>
                      <span className={styles.label}>Attendees</span>
                      <span className={styles.value}>{attendance}</span>
                    </div>
                    <div className={styles.infoField}>
                      <span className={styles.label}>Capacity</span>
                      <span className={styles.value}>{event.max_capacity ?? '—'}</span>
                    </div>
                  </div>

                  <div className={styles.infoRow}>
                    <div className={styles.infoField}>
                      <span className={styles.label}>Date</span>
                      <span className={styles.value}>{formattedDate ?? 'TBD'}</span>
                    </div>
                    <div className={styles.infoField}>
                      <span className={styles.label}>Time</span>
                      <span className={styles.value}>{formattedTime ?? 'TBD'}</span>
                    </div>
                  </div>

                  {event.description && (
                    <div className={styles.infoField}>
                      <span className={styles.label}>About</span>
                      <span className={styles.value}>{event.description}</span>
                    </div>
                  )}

                  <div className={styles.discountSection}>
                    <div className={styles.label}>Discount Code</div>
                    <div className={styles.discountCode}>
                      <span>{canSeeDiscount ? mockDiscountCode : 'Locked'}</span>

                      {canSeeDiscount && (
                        <>
                          <img
                            onLoad={(e) => e.currentTarget.classList.add('loaded')}
                            src={CopyIcon}
                            alt="Copy"
                            className={styles.copyIcon}
                            onClick={() => {
                              navigator.clipboard.writeText(mockDiscountCode);
                              setDiscountCopied(true);
                              setTimeout(() => setDiscountCopied(false), 1200);
                            }}
                          />

                          {discountCopied && (
                            <span className={styles.copiedText}>Copied!</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Group link */}
                  <div className={styles.infoRow}>
                    {group?.name && (
                      <div className={styles.infoField}>
                        <span className={styles.label}>Group</span>
                        <span className={styles.value}>
                          <Link to={`/groups/${group.id}`} className={styles.link}>
                            {group.name}
                          </Link>
                        </span>
                      </div>
                    )}

                    {/* Venue modal trigger */}
                    {venue?.venue_name && (
                      <div className={styles.infoField}>
                        <span className={styles.label}>Venue</span>
                        <span className={styles.value}>
                          <span
                            onClick={() => setVenueProfileOpen(true)}
                            className={styles.link}
                            style={{ cursor: 'pointer' }}
                          >
                            {venue.venue_name}
                          </span>
                        </span>
                      </div>
                    )}

                    {event.status && (
                      <div className={styles.infoField}>
                        <span className={styles.label}>Status</span>
                        <span className={styles.value}>{event.status}</span>
                      </div>
                    )}
                  </div>

                  {/* Event tags */}
                  <div className={styles.tagsSection}>
                    <div className={styles.tagsHeader}>
                      <span className={styles.label}>Interests</span>
                      <button
                        className={styles.tagsToggle}
                        onClick={() => setShowTags((prev) => !prev)}
                        aria-expanded={showTags}
                      >
                        {showTags ? 'Hide' : 'View'}
                        <span
                          className={`${styles.tagsChevron} ${showTags ? styles.tagsChevronOpen : ''}`}
                        >
                          ▼
                        </span>
                      </button>
                    </div>
                    <div
                      className={`${styles.tagsBody} ${showTags ? styles.tagsBodyOpen : ''}`}
                    >
                      {tags.length > 0 ? (
                        <div className={styles.tagsContainer}>
                          {tags.map((tag: string, i: number) => (
                            <span key={i} className={styles.tag}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className={styles.emptyTags}>
                          <p className={styles.emptyTagsText}>No interests yet</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Image Gallery */}
                  <div className={styles.gallerySection}>
                    <span className={styles.sectionTitle}>Gallery</span>
                    <div className={styles.emptyGallery}>
                      <p className={styles.emptyText}>No event photos available</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className={styles.panelFooter}>
            {/* RSVP button */}
            <button
              className={`${styles.rsvpBtn} ${isGoing ? styles.on : styles.off}`}
              onClick={handleRsvp}
              type="button"
              disabled={hasEnded}
            >
              {/* RSVP status */}
              {isGoing ? '✓ Going' : 'RSVP'}

              {/* RSVP count */}
              <span className={styles.rsvpCount}>· {count}</span>
            </button>
            {/* Show stats button if event ended */}
            {hasEnded && isPrivileged && (
              <button
                className={styles.statsBtn}
                onClick={() => setShowStatsModal(true)}
                type="button"
              >
                View Event Statistics
              </button>
            )}
          </div>
        </aside>
      </div>

      {showStatsModal && (
        <OrganizerEventStatisticsModal
          eventId={id}
          eventName={event?.event_name ?? 'Event'}
          onClose={() => setShowStatsModal(false)}
        />
      )}

      {venueProfileOpen &&
        event?.venue_id &&
        ReactDOM.createPortal(
          <VenueProfile
            venueId={event.venue_id}
            onClose={() => setVenueProfileOpen(false)}
          />,
          document.body,
        )}
    </div>
  );
}
